import { Dispatch, SetStateAction, useState } from 'react'
import { toast } from 'react-toastify'

import { Address, Company, IdentifiableObject, ModelMap, User } from '../models'
import { axiosInstance, isInvalidPhoneError } from '../helpers'

export const useOrganizationManagement = () => {
    const [loadingState, setLoadingState] = useState<OrgManagementLoadingState>(
        {
            companyList: false,
            userList: false,
            vendorList: false,
            userDetail: false,
            userActivity: false,
            userDetailActivity: false,
        },
    )
    const [userList, setUserList] = useState<User[]>([])
    const [vendorList, setVendorList] = useState<OrganizationVendor[]>([])
    const [companyList, setCompanyList] = useState<Company[]>([])
    const [userDetail, setUserDetail] = useState<User | null>(null)

    const [userListActivityMap, setUserActivityMap] = useState<
        ModelMap<UserActivity>
    >({})
    const [userDetailActivityMap, setUserDetailActivityMap] = useState<
        ModelMap<UserActivity>
    >({})

    const getOrganizationCompanyList = async (organizationId: number) => {
        try {
            setLoadingState((prev) => ({ ...prev, companyList: true }))
            const res = await axiosInstance.get(
                `company/ezfolio-company-list/`,
                {
                    params: { organization: organizationId },
                },
            )
            const companyList: Company[] = res.data
            setCompanyList(companyList)
            setLoadingState((prev) => ({ ...prev, companyList: false }))
            return companyList
        } catch (e) {
            toast.error('Error fetching organization companies')
            setLoadingState((prev) => ({ ...prev, companyList: false }))
            return Promise.reject(e)
        }
    }

    const getOrganizationUsers = async (organizationId: number) => {
        try {
            setLoadingState((prev) => ({ ...prev, userList: true }))
            const response = await axiosInstance.get('user/', {
                params: { organization: organizationId },
            })

            const organizationUsers: User[] = response.data
            setUserList(organizationUsers)
            setLoadingState((prev) => ({ ...prev, userList: false }))
            return organizationUsers
        } catch (e) {
            toast.error('Error fetching organization users')
            setLoadingState((prev) => ({ ...prev, userList: false }))
            return Promise.reject(e)
        }
    }

    const getOrganizationVendors = async (organizationId: number) => {
        try {
            setLoadingState((prev) => ({ ...prev, vendorList: true }))
            const response = await axiosInstance.get(
                'company/vendor/get-organization-vendors/',
                {
                    params: { organization_id: organizationId },
                },
            )

            const organizationVendors: OrganizationVendor[] = response.data
            setVendorList(organizationVendors)
            setLoadingState((prev) => ({ ...prev, vendorList: false }))
            return organizationVendors
        } catch (e) {
            toast.error('Error fetching organization vendors')
            setLoadingState((prev) => ({ ...prev, vendorList: false }))
            return Promise.reject(e)
        }
    }

    const getUserDetail = async (userId: number, organizationId: number) => {
        try {
            setLoadingState((prev) => ({ ...prev, userDetail: true }))
            const response = await axiosInstance.get(
                `user/get_ezfolio_root_user/${userId}/`,
                {
                    params: { organization: organizationId },
                },
            )
            const userDetail: User = response.data
            setUserDetail(userDetail)
            setLoadingState((prev) => ({ ...prev, userDetail: false }))
            return userDetail
        } catch (e) {
            toast.error('Error fetching user detail')
            setLoadingState((prev) => ({ ...prev, userDetail: false }))
            return Promise.reject(e)
        }
    }

    const bulkArchiveUser = async (request: BulkArchiveUserRequest) => {
        try {
            await axiosInstance.post(
                `user/${request.userId}/bulk_archive/`,
                request.body,
                {
                    params: request.params,
                },
            )
            return Promise.resolve()
        } catch (e: any) {
            toast.error(e.response.data.message)
            return Promise.reject(e)
        }
    }

    const bulkAddUserToWorkspaces = async (
        request: BulkAddUserToWorkspacesRequest,
    ) => {
        try {
            const response = await axiosInstance.post(
                `user/bulk_create_workspace_users/`,
                request.body,
                {
                    params: request.params,
                },
            )

            _insertOrUpdateIdentifiable(response.data, setUserList)

            return Promise.resolve()
        } catch (e: any) {
            if (!isInvalidPhoneError(e)) {
                toast.error(e.response.data.message)
            }
            return Promise.reject(e)
        }
    }

    const getUserListActivity = async (organizationId: number) => {
        try {
            setLoadingState((prev) => ({ ...prev, userActivity: true }))
            const response = await axiosInstance.get('ezfolio-request-log/', {
                params: { organization: organizationId },
            })
            const userActivityMap: ModelMap<UserActivity> = response.data
            setUserActivityMap(userActivityMap)
            setLoadingState((prev) => ({ ...prev, userActivity: false }))
            return userActivityMap
        } catch (e: any) {
            toast.error(e.response.data.message)
            setLoadingState((prev) => ({ ...prev, userActivity: false }))
            return Promise.reject(e)
        }
    }

    const getUserDetailActivity = async (
        userId: number,
        organizationId: number,
    ) => {
        try {
            setLoadingState((prev) => ({ ...prev, userDetailActivity: true }))
            const response = await axiosInstance.get(
                `ezfolio-user-request-log/${userId}/`,
                {
                    params: { organization: organizationId },
                },
            )
            const userActivityMap: ModelMap<UserActivity> = response.data
            setUserDetailActivityMap(userActivityMap)
            setLoadingState((prev) => ({ ...prev, userDetailActivity: false }))
            return userActivityMap
        } catch (e: any) {
            toast.error(e.response.data.message)
            setLoadingState((prev) => ({ ...prev, userDetailActivity: false }))
            return Promise.reject(e)
        }
    }

    const markAsRegionalManager = async (
        userId: number,
        organizationId: number,
    ) => {
        try {
            const response = await axiosInstance.post(
                `user/${userId}/set_regional_manager/`,
                {},
                {
                    params: { organization: organizationId },
                },
            )

            const user: User = response.data
            _insertOrUpdateIdentifiable(user, setUserList)
            return Promise.resolve()
        } catch (e: any) {
            toast.error(e.response.data.message)
            return Promise.reject(e)
        }
    }

    const markAsPropertyManager = async (
        userId: number,
        organizationId: number,
        companyId: number,
    ) => {
        try {
            const response = await axiosInstance.post(
                `user/${userId}/set_property_manager/`,
                { company_id: companyId },
                {
                    params: { organization: organizationId },
                },
            )

            const user: User = response.data
            _insertOrUpdateIdentifiable(user, setUserList)
            return Promise.resolve()
        } catch (e: any) {
            toast.error(e.response.data.message)
            return Promise.reject(e)
        }
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    return {
        loadingState,
        userList,
        vendorList,
        companyList,
        userDetail,
        userListActivityMap,
        userDetailActivityMap,
        getOrganizationCompanyList,
        getOrganizationUsers,
        getOrganizationVendors,
        getUserDetail,
        setUserDetail,
        bulkArchiveUser,
        setUserList,
        bulkAddUserToWorkspaces,
        getUserListActivity,
        getUserDetailActivity,
        setUserDetailActivityMap,
        markAsRegionalManager,
        markAsPropertyManager,
    }
}

export interface OrgManagementLoadingState {
    companyList: boolean
    userList: boolean
    vendorList: boolean
    userDetail: boolean
    userActivity: boolean
    userDetailActivity: boolean
}

export interface BulkArchiveUserRequest {
    userId: number
    body: {
        company_ids: number[]
    }
    params: {
        organization?: number
    }
}

export interface BulkAddUserToWorkspacesRequest {
    body: {
        company_ids: number[]
        organization_id: number
        user_id?: number
        permission_roles?: number[]
        email?: string
        name?: string
        phone?: string
        cell_phone?: string
    }
    params: {
        organization?: number
    }
}

export interface UserActivity extends IdentifiableObject {
    latest_timestamp: string
    log_count: number
}

export interface OrganizationVendorProperty extends IdentifiableObject {
    name: string
}

export interface OrganizationVendor extends IdentifiableObject {
    name: string
    invoice_code: string | null
    in_house: boolean | null
    email: string | null
    primary_address: Address | null
    properties: OrganizationVendorProperty[]
}
